import type { FormatBytesOptions } from './types';
import {
  DECIMAL_UNITS,
  BINARY_UNITS,
  DECIMAL_BASE,
  BINARY_BASE,
  UNIT_MAP,
} from './constants';

export type { FormatBytesOptions };

/**
 * Formats a number of bytes into a human-readable string.
 * @param bytes The number of bytes to format.
 * @param options Optional formatting configuration.
 * @returns A human-readable string representation of the byte size.
 * @throws {TypeError} If input `bytes` is not a number.
 */
export function formatBytes(
  bytes: number,
  options?: FormatBytesOptions
): string {
  if (typeof bytes !== 'number') {
    throw new TypeError('Input bytes must be a number');
  }

  const {
    decimalPlaces = 2,
    useBinary = false,
    fixedDecimals = false,
    includeUnitSpace = true,
  } = options || {};

  const units = useBinary ? BINARY_UNITS : DECIMAL_UNITS;
  const base = useBinary ? BINARY_BASE : DECIMAL_BASE;
  const space = includeUnitSpace ? ' ' : '';

  if (bytes === 0) {
    return `0${space}Bytes`;
  }

  const isNegative = bytes < 0;
  const absoluteBytes = Math.abs(bytes);

  const unitIndex =
    absoluteBytes < 1
      ? 0
      : Math.min(
          Math.floor(Math.log(absoluteBytes) / Math.log(base)),
          units.length - 1
        );

  const value = absoluteBytes / Math.pow(base, unitIndex);
  const formattedValue = fixedDecimals
    ? value.toFixed(decimalPlaces)
    : parseFloat(value.toFixed(decimalPlaces)).toString();

  const sign = isNegative ? '-' : '';
  const unit = units[unitIndex];

  return `${sign}${formattedValue}${space}${unit}`;
}

/**
 * Parses a human-readable byte string (e.g., "512KB", "1.5 GiB") into a number of bytes.
 * Supports both decimal (KB, MB) and binary (KiB, MiB) units case-insensitively.
 * @param sizeString The human-readable byte string to parse.
 * @returns The number of bytes, or null if the string is unparseable.
 * @throws {TypeError} If input `sizeString` is not a string.
 */
export function parseBytes(sizeString: string): number | null {
  if (typeof sizeString !== 'string') {
    throw new TypeError('Input sizeString must be a string');
  }

  const trimmed = sizeString.trim();
  if (!trimmed) {
    return null;
  }

  // Regex to match number (with optional decimal) followed by optional space and unit
  const match = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*([a-z]+)?$/i);
  if (!match) {
    return null;
  }

  const [, valueStr, unit] = match;
  const value = parseFloat(valueStr!);

  if (isNaN(value)) {
    return null;
  }

  // If no unit provided, assume bytes
  if (!unit) {
    return value;
  }

  // Look up the unit in our map (case-insensitive)
  const unitLower = unit.toLowerCase();
  const multiplier = UNIT_MAP[unitLower];

  if (multiplier === undefined) {
    return null;
  }

  return value * multiplier;
}
